import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { LogOut, Hospital, ArrowRight, Plus } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";

export default function SimulationLobby() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [simulations, setSimulations] = useState([]);
  const [showJoin, setShowJoin] = useState(false);
  const [joinForm, setJoinForm] = useState({ join_code: "" });

  useEffect(() => { loadSimulations(); }, []);

  const loadSimulations = async () => {
    try { const res = await axios.get(`${API}/simulations`); setSimulations(res.data); }
    catch { /* silent */ }
  };

  const joinHospital = async () => {
    try {
      const res = await axios.post(`${API}/hospitals/join`, joinForm);
      toast.success(t("lobby.joinedHospital") || "Te has unido al hospital");
      setShowJoin(false);
      loadSimulations();
      navigate(`/simulation/${res.data.simulation_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al unirse"); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="simulation-lobby">
      <header className="bg-white/90 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-medsim.png" alt="MedSim" className="h-8 rounded-md" />
            <span className="text-lg font-heading font-semibold text-slate-900 tracking-tight">MedSim</span>
            <span className="text-xs text-slate-400 font-medium">/</span>
            <span className="text-sm text-slate-500">{user?.name}</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <LanguageSwitcher />
            <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">{t("lobby.title")}</h2>
            <p className="text-sm text-slate-500 mt-1">{t("lobby.subtitle") || "Selecciona una simulación o únete a un hospital."}</p>
          </div>
          <Button onClick={() => setShowJoin(true)} className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm" data-testid="join-hospital-btn">
            <Plus className="w-4 h-4 mr-2" />{t("lobby.joinHospital")}
          </Button>
        </div>

        {simulations.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center shadow-sm">
            <Hospital className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-base font-heading font-medium text-slate-600">{t("lobby.noSimulations")}</p>
            <p className="text-sm text-slate-400 mt-2">{t("lobby.useCodeHint") || "Usa tu código de unión para entrar a un hospital."}</p>
            <Button variant="outline" onClick={() => setShowJoin(true)} className="mt-4 border-slate-300" data-testid="join-hospital-btn-empty">
              <Plus className="w-4 h-4 mr-2" />{t("lobby.joinHospital")}
            </Button>
          </div>
        ) : (
          <div className="grid gap-3">
            {simulations.map(sim => (
              <div key={sim.id} className="bg-white border border-slate-200 rounded-lg p-5 flex items-center justify-between shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer group" onClick={() => navigate(`/simulation/${sim.id}`)} data-testid={`sim-entry-${sim.id}`}>
                <div>
                  <h3 className="font-heading font-medium text-slate-900">{sim.name}</h3>
                  <div className="flex items-center gap-3 mt-1.5">
                    <Badge variant={sim.status === "active" ? "default" : "secondary"} className="text-[10px]">{sim.status === "active" ? "Activa" : sim.status}</Badge>
                    <span className="text-sm text-slate-400">{t("common.round")} {sim.current_round} / {sim.rounds_count}</span>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-slate-900 transition-colors" />
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showJoin} onOpenChange={setShowJoin}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{t("lobby.joinHospital")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("lobby.joinCode")}</Label>
              <Input value={joinForm.join_code} onChange={e => setJoinForm({...joinForm, join_code: e.target.value.toUpperCase()})} placeholder="ABC123" className="mt-1.5 font-mono uppercase tracking-widest border-slate-300" data-testid="join-code-input" />
              <p className="text-xs text-slate-400 mt-1.5">{t("lobby.codeHint") || "Introduce el código proporcionado por tu instructor."}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJoin(false)}>{t("common.cancel")}</Button>
            <Button onClick={joinHospital} className="bg-slate-900 hover:bg-slate-800" data-testid="confirm-join-btn">{t("lobby.joinHospital")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
